import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ipdBillingAPI } from '../../utils/api';
import { toast } from 'react-toastify';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (v) => parseFloat(v) || 0;

const inp = { padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' };
const lbl = { fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' };
const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: 18, marginBottom: 16 };

// ─── Row component for charge lines ──────────────────────────────────────────
const ChargeRow = ({ label, value, editable, onChange, sub, highlight }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8, alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
    <span style={{ fontSize: 13, color: sub ? '#6b7280' : highlight ? '#1e3a5f' : '#374151', fontWeight: highlight ? 700 : 400, paddingLeft: sub ? 16 : 0 }}>{label}</span>
    {editable
      ? <input type="number" min="0" value={value} onChange={e => onChange(e.target.value)}
          style={{ ...inp, textAlign: 'right', fontWeight: 600, color: '#1e3a5f' }} />
      : <div style={{ textAlign: 'right', fontSize: 13, fontWeight: highlight ? 800 : 600, color: highlight ? '#0f766e' : '#111827', paddingRight: 4 }}>
          Rs. {fmt(value)}
        </div>
    }
  </div>
);

export default function IPDBilling() {
  const location = useLocation();
  const [tab, setTab] = useState('billing');

  // ── Billing tab state ──────────────────────────────────────────────────────
  const [admissionNo, setAdmissionNo] = useState(location.state?.admissionNumber || '');
  const [loading, setLoading]         = useState(false);
  const [billData, setBillData]       = useState(null);
  const [saving, setSaving]           = useState(false);

  // Editable fields
  const [otherCharges, setOtherCharges]   = useState(0);
  const [otherDesc, setOtherDesc]         = useState('');
  const [discountPct, setDiscountPct]     = useState(0);
  const [advancePaid, setAdvancePaid]     = useState(0);
  const [paymentMode, setPaymentMode]     = useState('Cash');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [notes, setNotes]                 = useState('');

  // ── Consultant rates tab state ─────────────────────────────────────────────
  const [consultants, setConsultants]   = useState([]);
  const [doctors, setDoctors]           = useState([]);
  const [newDoctor, setNewDoctor]       = useState('');
  const [newRate, setNewRate]           = useState('');
  const [savingC, setSavingC]           = useState(false);
  const [doctorCustom, setDoctorCustom] = useState(false);

  useEffect(() => {
    if (tab === 'rates') { loadConsultants(); loadDoctors(); }
  }, [tab]);

  useEffect(() => {
    if (location.state?.admissionNumber) searchBill();
  }, []);

  const loadConsultants = async () => {
    try { const r = await ipdBillingAPI.getConsultants(); setConsultants(r.data || []); }
    catch { toast.error('Failed to load rates'); }
  };

  const loadDoctors = async () => {
    try { const r = await ipdBillingAPI.getDoctors(); setDoctors(r.data || []); }
    catch {}
  };

  // ── Calculate derived totals ───────────────────────────────────────────────
  const derived = useCallback(() => {
    if (!billData) return {};
    const c = billData.charges;
    const subtotalBefore = num(c.room_charges) + num(c.consult_charges) + num(c.lab_charges) +
      num(c.radiology_charges) + num(c.pharmacy_charges) + num(c.procedure_charges) + num(otherCharges);
    const discAmt   = +(subtotalBefore * num(discountPct) / 100).toFixed(2);
    const subtotal  = +(subtotalBefore - discAmt).toFixed(2);
    const balance   = +Math.max(0, subtotal - num(advancePaid)).toFixed(2);
    return { subtotalBefore, discAmt, subtotal, balance };
  }, [billData, otherCharges, discountPct, advancePaid]);

  const totals = derived();

  // ── Search admission ───────────────────────────────────────────────────────
  const searchBill = async () => {
    if (!admissionNo.trim()) return toast.error('Enter Admission Number');
    setLoading(true);
    setBillData(null);
    try {
      const { default: axios } = await import('axios');
      const token = localStorage.getItem('hims_token');
      const admR = await axios.get(`/api/ipd/search-admission`, {
        params: { q: admissionNo.trim() },
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = admR.data || [];
      if (!list.length) { toast.error('Admission not found'); setLoading(false); return; }
      const adm = list[0];

      const r = await ipdBillingAPI.calculateBill(adm.id);
      const d = r.data;
      setBillData(d);
      setOtherCharges(d.charges.other_charges || 0);
      setOtherDesc(d.charges.other_charges_desc || '');
      setDiscountPct(d.totals.discount_pct || 0);
      setAdvancePaid(d.totals.advance_paid || 0);
      setPaymentMode('Cash');
      setPaymentStatus(d.payment_status || 'Pending');
      setNotes('');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Search failed');
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!billData) return;
    setSaving(true);
    try {
      const c = billData.charges;
      const t = totals;
      await ipdBillingAPI.saveBill({
        admission_id:      billData.admission.id,
        patient_id:        billData.admission.patient_id,
        room_days:         c.days,
        room_rate:         c.ward_rate,
        room_charges:      c.room_charges,
        consult_charges:   c.consult_charges,
        lab_charges:       c.lab_charges,
        radiology_charges: c.radiology_charges,
        pharmacy_charges:  c.pharmacy_charges,
        procedure_charges: c.procedure_charges,
        other_charges:     num(otherCharges),
        other_charges_desc: otherDesc,
        discount_pct:      num(discountPct),
        discount_amount:   t.discAmt,
        subtotal:          t.subtotal,
        advance_paid:      num(advancePaid),
        balance_due:       t.balance,
        payment_mode:      paymentMode,
        payment_status:    paymentStatus,
        notes,
      });
      toast.success('Bill saved successfully');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    }
    setSaving(false);
  };

  const printBill = () => {
    if (!billData) return;
    const a = billData.admission;
    const c = billData.charges;
    const t = totals;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>IPD Bill — ${a.admission_number}</title>
<style>
  body{font-family:Arial,sans-serif;font-size:12px;margin:24px;color:#111}
  h2{text-align:center;margin:0;font-size:18px;color:#1e3a5f}
  .sub{text-align:center;color:#555;margin-bottom:16px;font-size:11px}
  .patient-box{background:#f0f4f8;padding:10px 14px;border-radius:6px;margin-bottom:16px;display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px;font-size:11px}
  .patient-box b{color:#555}
  .section{font-weight:bold;background:#1e3a5f;color:#fff;padding:5px 10px;margin:14px 0 6px;border-radius:4px;font-size:12px}
  table{width:100%;border-collapse:collapse}
  td,th{padding:5px 8px;border-bottom:1px solid #e5e7eb;font-size:12px}
  th{background:#f8fafc;font-weight:700;text-align:left;color:#374151}
  .right{text-align:right}
  .total-row td{font-weight:700;background:#f0fdf4;color:#065f46;font-size:13px}
  .balance-row td{font-weight:800;background:#1e3a5f;color:#fff;font-size:14px}
  .footer{margin-top:24px;font-size:10px;color:#999;text-align:center;border-top:1px solid #e5e7eb;padding-top:8px}
</style></head><body>
<h2>IPD Bill / Discharge Invoice</h2>
<div class="sub">Generated: ${new Date().toLocaleString('en-PK')} | Bill Status: ${paymentStatus}</div>
<div class="patient-box">
  <div><b>Patient:</b> ${a.patient_name}</div>
  <div><b>MR#:</b> ${a.mrn}</div>
  <div><b>Admission#:</b> ${a.admission_number}</div>
  <div><b>Age/Gender:</b> ${a.age_years || '-'} yrs / ${a.gender || '-'}</div>
  <div><b>Ward:</b> ${a.ward_name || '-'}</div>
  <div><b>Consultant:</b> ${a.consultant || '-'}</div>
  <div><b>Admitted:</b> ${a.admission_time ? new Date(a.admission_time).toLocaleDateString('en-PK') : '-'}</div>
  <div><b>Discharged:</b> ${a.discharge_time ? new Date(a.discharge_time).toLocaleDateString('en-PK') : 'Still Admitted'}</div>
  <div><b>Duration:</b> ${c.days} day(s)</div>
</div>
<div class="section">Charge Details</div>
<table>
  <tr><th>Description</th><th>Rate</th><th>Days/Qty</th><th class="right">Amount (Rs.)</th></tr>
  <tr><td>Room / Ward Charges</td><td>Rs. ${fmt(c.ward_rate)}/day</td><td>${c.days}</td><td class="right">${fmt(c.room_charges)}</td></tr>
  <tr><td>Consultant Fee (${a.consultant || '-'})</td><td>Rs. ${fmt(c.consult_rate)}/day</td><td>${c.days}</td><td class="right">${fmt(c.consult_charges)}</td></tr>
  <tr><td>Laboratory Charges</td><td>—</td><td>—</td><td class="right">${fmt(c.lab_charges)}</td></tr>
  <tr><td>Radiology / Imaging</td><td>—</td><td>—</td><td class="right">${fmt(c.radiology_charges)}</td></tr>
  <tr><td>Pharmacy / Medicines</td><td>—</td><td>—</td><td class="right">${fmt(c.pharmacy_charges)}</td></tr>
  <tr><td>Procedures</td><td>—</td><td>—</td><td class="right">${fmt(c.procedure_charges)}</td></tr>
  ${num(otherCharges) > 0 ? `<tr><td>Other: ${otherDesc || 'Misc'}</td><td>—</td><td>—</td><td class="right">${fmt(otherCharges)}</td></tr>` : ''}
  <tr><td colspan="3" style="font-weight:700">Sub-Total (Before Discount)</td><td class="right" style="font-weight:700">${fmt(t.subtotalBefore)}</td></tr>
  <tr><td colspan="3">Discount (${discountPct}%)</td><td class="right" style="color:#dc2626">- ${fmt(t.discAmt)}</td></tr>
  <tr class="total-row"><td colspan="3">Net Total</td><td class="right">${fmt(t.subtotal)}</td></tr>
  <tr><td colspan="3">Advance Paid</td><td class="right" style="color:#059669">- ${fmt(advancePaid)}</td></tr>
  <tr class="balance-row"><td colspan="3">Balance Due</td><td class="right">Rs. ${fmt(t.balance)}</td></tr>
</table>
${notes ? `<p style="margin-top:12px;font-size:11px"><b>Notes:</b> ${notes}</p>` : ''}
<p style="font-size:11px;margin-top:8px"><b>Payment Mode:</b> ${paymentMode} &nbsp;|&nbsp; <b>Status:</b> ${paymentStatus}</p>
<div class="footer">HIMS — Hospital Information Management System &nbsp;|&nbsp; This is a computer generated invoice</div>
<script>window.onload=()=>{window.print()}</script>
</body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); }
  };

  // ── Consultant rates tab ───────────────────────────────────────────────────
  const handleSaveConsultant = async () => {
    if (!newDoctor.trim() || !newRate) return toast.error('Enter doctor name and rate');
    setSavingC(true);
    try {
      await ipdBillingAPI.saveConsultant({ doctor_name: newDoctor.trim(), rate_per_day: parseFloat(newRate) });
      toast.success('Rate saved');
      setNewDoctor(''); setNewRate('');
      loadConsultants();
    } catch (e) { toast.error('Failed to save'); }
    setSavingC(false);
  };

  const handleDeleteConsultant = async (id) => {
    if (!window.confirm('Delete this rate?')) return;
    try { await ipdBillingAPI.deleteConsultant(id); loadConsultants(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  return (
    <div style={{ maxWidth: 900 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: 28 }}>🧾</span>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e3a5f', margin: 0 }}>IPD Billing</h2>
          <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Auto-calculated room, consultant, lab, pharmacy charges</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 18 }}>
        {[['billing','🧾 Generate Bill'],['rates','⚙️ Consultant Rates']].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 600,
            background: tab === k ? '#1e3a5f' : '#f1f5f9',
            color: tab === k ? '#fff' : '#374151',
          }}>{l}</button>
        ))}
      </div>

      {/* ── BILLING TAB ─────────────────────────────────────────────────────── */}
      {tab === 'billing' && (<>

        {/* Search */}
        <div style={{ ...card, display: 'flex', gap: 10, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Admission # / MR# / Patient Name</label>
            <input value={admissionNo} onChange={e => setAdmissionNo(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && searchBill()}
              placeholder="e.g. IPD-2026-001 or MR-00123"
              style={inp} />
          </div>
          <button onClick={searchBill} disabled={loading}
            style={{ padding: '8px 22px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', height: 36 }}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>

        {billData && (<>
          {/* Patient Info */}
          <div style={{ ...card, borderLeft: `4px solid ${billData.payment_status === 'Paid' ? '#16a34a' : billData.payment_status === 'Partial' ? '#d97706' : '#dc2626'}` }}>
            {/* Payment status banner */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f' }}>Patient Details</span>
              <span style={{
                padding: '4px 14px', borderRadius: 20, fontWeight: 700, fontSize: 12,
                background: billData.payment_status === 'Paid' ? '#dcfce7' : billData.payment_status === 'Partial' ? '#fef3c7' : '#fee2e2',
                color: billData.payment_status === 'Paid' ? '#15803d' : billData.payment_status === 'Partial' ? '#b45309' : '#b91c1c',
                border: `1px solid ${billData.payment_status === 'Paid' ? '#86efac' : billData.payment_status === 'Partial' ? '#fde68a' : '#fca5a5'}`,
              }}>
                {billData.payment_status === 'Paid' ? '✓ BILL PAID' : billData.payment_status === 'Partial' ? '⚠ PARTIAL PAYMENT' : '✗ BILL PENDING'}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {[
                ['Patient', billData.admission.patient_name],
                ['MR#', billData.admission.mrn],
                ['Admission#', billData.admission.admission_number],
                ['Ward', billData.admission.ward_name || '—'],
                ['Consultant', billData.admission.consultant || '—'],
                ['Duration', `${billData.charges.days} day(s)`],
                ['Admitted', billData.admission.admission_time ? new Date(billData.admission.admission_time).toLocaleDateString('en-PK') : '—'],
                ['Discharged', billData.admission.discharge_time ? new Date(billData.admission.discharge_time).toLocaleDateString('en-PK') : 'Still Admitted'],
                ['Payment Type', billData.admission.payment_type || '—'],
                ['Bill #', billData.bill_number || '(not saved yet)'],
                ['Admission Status', billData.admission.status || '—'],
              ].map(([l,v]) => (
                <div key={l}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>{l}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>{v}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

            {/* Left: Charges */}
            <div style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 10, borderBottom: '2px solid #e2e8f0', paddingBottom: 6 }}>
                Charge Breakdown
              </div>
              <ChargeRow label={`Room/Ward (Rs.${fmt(billData.charges.ward_rate)}/day × ${billData.charges.days} days)`} value={billData.charges.room_charges} />
              <ChargeRow label={`Consultant Fee (Rs.${fmt(billData.charges.consult_rate)}/day × ${billData.charges.days} days)`} value={billData.charges.consult_charges} />
              <ChargeRow label="Laboratory" value={billData.charges.lab_charges} />
              <ChargeRow label="Radiology / Imaging" value={billData.charges.radiology_charges} />
              <ChargeRow label="Pharmacy / Medicines" value={billData.charges.pharmacy_charges} />
              <ChargeRow label="Procedures" value={billData.charges.procedure_charges} />

              {/* Other charges — editable */}
              <div style={{ marginTop: 8 }}>
                <label style={lbl}>Other Charges (Rs.)</label>
                <input type="number" min="0" value={otherCharges} onChange={e => setOtherCharges(e.target.value)} style={inp} />
                <input value={otherDesc} onChange={e => setOtherDesc(e.target.value)}
                  placeholder="Description (e.g. Ambulance, Oxygen)" style={{ ...inp, marginTop: 4 }} />
              </div>
            </div>

            {/* Right: Totals */}
            <div>
              <div style={card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 10, borderBottom: '2px solid #e2e8f0', paddingBottom: 6 }}>
                  Totals & Adjustments
                </div>

                <ChargeRow label="Sub-Total (Before Discount)" value={totals.subtotalBefore} highlight />

                {/* Discount % */}
                <div style={{ padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#374151' }}>Discount %</span>
                    <input type="number" min="0" max="100" step="0.5" value={discountPct}
                      onChange={e => setDiscountPct(e.target.value)}
                      style={{ ...inp, textAlign: 'right', color: '#dc2626', fontWeight: 600 }} />
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                    Discount Amount: Rs. {fmt(totals.discAmt)}
                  </div>
                </div>

                <ChargeRow label="Net Total (After Discount)" value={totals.subtotal} highlight />

                {/* Advance Paid */}
                <div style={{ padding: '7px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#374151' }}>Advance Paid (Rs.)</span>
                    <input type="number" min="0" value={advancePaid}
                      onChange={e => setAdvancePaid(e.target.value)}
                      style={{ ...inp, textAlign: 'right', color: '#059669', fontWeight: 600 }} />
                  </div>
                </div>

                {/* Balance Due */}
                <div style={{ background: totals.balance <= 0 ? '#f0fdf4' : '#fef2f2', border: `1px solid ${totals.balance <= 0 ? '#bbf7d0' : '#fecaca'}`, borderRadius: 8, padding: '12px 14px', marginTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: totals.balance <= 0 ? '#065f46' : '#dc2626' }}>Balance Due</span>
                  <span style={{ fontSize: 20, fontWeight: 800, color: totals.balance <= 0 ? '#065f46' : '#dc2626' }}>Rs. {fmt(totals.balance)}</span>
                </div>
              </div>

              {/* Payment & Notes */}
              <div style={card}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 10 }}>Payment Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div>
                    <label style={lbl}>Payment Mode</label>
                    <select value={paymentMode} onChange={e => setPaymentMode(e.target.value)} style={{ ...inp, background: '#fff' }}>
                      {['Cash','Card','Bank Transfer','Insurance','Corporate','Cheque'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Payment Status</label>
                    <select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)} style={{ ...inp, background: '#fff' }}>
                      {['Pending','Partial','Paid','Waived'].map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <label style={lbl}>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                  placeholder="Any additional notes..." style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '10px 28px', background: saving ? '#9ca3af' : '#0f766e', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving...' : '✓ Save Bill'}
            </button>
            <button onClick={printBill}
              style={{ padding: '10px 24px', background: '#1e3a5f', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
              🖨️ Print Bill
            </button>
          </div>
        </>)}
      </>)}

      {/* ── CONSULTANT RATES TAB ────────────────────────────────────────────── */}
      {tab === 'rates' && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 14 }}>Consultant Charges (Per Day)</div>

          {/* Add / Update */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 160px auto', gap: 10, marginBottom: 18, alignItems: 'flex-end' }}>
            <div>
              <label style={lbl}>Doctor Name</label>
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                <button type="button" onClick={() => { setDoctorCustom(true); setNewDoctor(''); }} title="Type manually" style={{
                  width: 34, height: 34, border: '1px solid', borderRadius: 5, cursor: 'pointer',
                  fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: doctorCustom ? '#1e3a5f' : '#f1f5f9',
                  color: doctorCustom ? '#fff' : '#64748b',
                  borderColor: doctorCustom ? '#1e3a5f' : '#e2e8f0', flexShrink: 0,
                }}>✏️</button>
                <button type="button" onClick={() => { setDoctorCustom(false); setNewDoctor(''); }} title="Select from list" style={{
                  padding: '0 8px', height: 34, border: '1px solid', borderRadius: 5, cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3,
                  background: !doctorCustom ? '#1e3a5f' : '#f1f5f9',
                  color: !doctorCustom ? '#fff' : '#64748b',
                  borderColor: !doctorCustom ? '#1e3a5f' : '#e2e8f0', flexShrink: 0, whiteSpace: 'nowrap',
                }}>List ↩</button>
                {doctorCustom
                  ? <input value={newDoctor} onChange={e => setNewDoctor(e.target.value)}
                      placeholder="Type doctor name..." style={{ ...inp, flex: 1 }} />
                  : <select value={newDoctor} onChange={e => setNewDoctor(e.target.value)}
                      style={{ ...inp, flex: 1, background: '#fff' }}>
                      <option value="">— Select Doctor —</option>
                      {doctors.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                    </select>
                }
              </div>
            </div>
            <div>
              <label style={lbl}>Rate / Day (Rs.)</label>
              <input type="number" min="0" value={newRate} onChange={e => setNewRate(e.target.value)}
                placeholder="500" style={inp} />
            </div>
            <button onClick={handleSaveConsultant} disabled={savingC}
              style={{ padding: '8px 18px', background: '#0f766e', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: 'pointer', height: 36 }}>
              {savingC ? '...' : '+ Save'}
            </button>
          </div>

          {/* List */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Doctor Name', 'Rate / Day', 'Action'].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontWeight: 700, color: '#475569', borderBottom: '2px solid #e2e8f0', fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {consultants.length === 0
                ? <tr><td colSpan={3} style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>No rates configured yet</td></tr>
                : consultants.map((c, i) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 600, color: '#1e3a5f' }}>{c.doctor_name}</td>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: '#0f766e' }}>Rs. {fmt(c.rate_per_day)} / day</td>
                    <td style={{ padding: '9px 12px' }}>
                      <button onClick={() => { setNewDoctor(c.doctor_name); setNewRate(c.rate_per_day); }}
                        style={{ marginRight: 6, padding: '3px 10px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Edit</button>
                      <button onClick={() => handleDeleteConsultant(c.id)}
                        style={{ padding: '3px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Delete</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
